/**
 * REVOLOR Predictive Search
 * Custom Element: <revolor-search>
 *
 * Teveo-style search overlay with Section Rendering API integration.
 * Mobile: fullscreen overlay | Desktop: dropdown below header
 */

class RevolorSearch extends HTMLElement {
  constructor() {
    super();

    this.overlay = this.querySelector('.revolor-search__overlay');
    this.panel = this.querySelector('.revolor-search__panel');
    this.input = this.querySelector('.revolor-search__input');
    this.closeBtn = this.querySelector('.revolor-search__close');
    this.backdrop = this.querySelector('.revolor-search__backdrop');
    this.resultsContainer = this.querySelector('[data-revolor-search-results]');
    this.skeleton = this.querySelector('[data-skeleton]');
    this.statusElement = this.querySelector('.revolor-search__status');
    this.form = this.querySelector('.revolor-search__form');
    this.defaultContent = this.querySelector('[data-default-content]');
    this.defaultProductsContainer = this.querySelector('[data-default-products]');
    this.defaultProductsTemplate = document.querySelector('[data-search-default-products]');
    this.viewAllSlot = this.querySelector('[data-view-all-slot]');
    this.defaultProductsLoaded = false;

    this.isOpen = false;
    this.searchTerm = '';
    this.cachedResults = {};
    this.abortController = null;
    this.selectedIndex = -1;

    this.setupEventListeners();
  }

  setupEventListeners() {
    // Open triggers (mobile icon + desktop pill)
    this.querySelectorAll('.revolor-search__trigger--mobile, .revolor-search__pill').forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    // Close button
    this.closeBtn.addEventListener('click', () => this.close());

    // Click outside panel to close (desktop blur area)
    this.overlay.addEventListener('click', (e) => {
      if (!e.target.closest('.revolor-search__panel')) {
        this.close();
      }
    });

    // Debounced input for API search
    this.input.addEventListener(
      'input',
      debounce(() => this.onChange(), 300)
    );

    // Keyboard navigation within overlay
    this.overlay.addEventListener('keydown', (e) => this.onKeydown(e));

    // Form submit
    this.form.addEventListener('submit', (e) => this.onFormSubmit(e));
  }

  open() {
    if (this.isOpen) return;
    this.isOpen = true;

    const isMobile = window.innerWidth < 990;

    // Move overlay to body to escape header stacking context
    document.body.appendChild(this.overlay);

    // Measure header BEFORE scroll lock
    let headerBottom = 60;
    if (isMobile) {
      const headerSection = document.querySelector('#shopify-section-header') || document.querySelector('.section-header');
      if (headerSection) {
        headerBottom = headerSection.getBoundingClientRect().bottom;
      }
    }

    // Scroll lock
    this._savedScrollY = window.scrollY;
    if (isMobile) {
      // Measure all header group sections BEFORE body shift
      // Shopify renders sections as siblings with class, no wrapper div
      this._fixedSections = [];
      let yOffset = 0;
      const headerSections = document.querySelectorAll('.shopify-section-group-header-group');
      for (const section of headerSections) {
        const rect = section.getBoundingClientRect();
        if (rect.height > 0 && rect.bottom > 0) {
          this._fixedSections.push({ el: section, top: yOffset, height: rect.height });
          yOffset += rect.height;
        }
      }
      headerBottom = yOffset || headerBottom;

      // Now lock body
      document.body.style.position = 'fixed';
      document.body.style.top = '-' + this._savedScrollY + 'px';
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      document.body.classList.add('overflow-hidden');

      // Apply fixed positioning to each section AFTER body is locked
      for (const s of this._fixedSections) {
        s.el.style.setProperty('position', 'fixed', 'important');
        s.el.style.setProperty('top', s.top + 'px', 'important');
        s.el.style.setProperty('left', '0', 'important');
        s.el.style.setProperty('right', '0', 'important');
        s.el.style.setProperty('z-index', '1002', 'important');
        s.el.style.setProperty('height', s.height + 'px', 'important');
      }

      this.overlay.style.top = headerBottom + 'px';
      this.overlay.style.height = 'calc(100dvh - ' + headerBottom + 'px)';
    } else {
      document.body.style.top = '-' + this._savedScrollY + 'px';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.classList.add('overflow-hidden');
    }
    document.body.classList.add('revolor-search-open');

    this.showDefaultSuggestions();
    this.overlay.setAttribute('aria-hidden', 'false');

    // Close menu drawer if open
    const drawerDetails = document.getElementById('Details-menu-drawer-container');
    if (drawerDetails && drawerDetails.open) {
      const summary = drawerDetails.querySelector('summary');
      if (summary) summary.click();
    }

    requestAnimationFrame(() => {
      this.input.focus();
    });

    this._escapeHandler = (e) => {
      if (e.key === 'Escape') this.close();
    };
    document.addEventListener('keydown', this._escapeHandler);
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;

    // Start slide-out animation
    this.overlay.setAttribute('aria-hidden', 'true');

    // Wait for slide-out transition to finish, then clean up
    const cleanup = () => {
      document.body.classList.remove('overflow-hidden');
      document.body.classList.remove('revolor-search-open');
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      document.body.style.top = '';

      if (this._fixedSections) {
        for (const s of this._fixedSections) {
          s.el.style.removeProperty('position');
          s.el.style.removeProperty('top');
          s.el.style.removeProperty('left');
          s.el.style.removeProperty('right');
          s.el.style.removeProperty('z-index');
          s.el.style.removeProperty('height');
        }
        this._fixedSections = null;
      }

      window.scrollTo(0, this._savedScrollY || 0);
    };

    // Match CSS transition duration (350ms)
    setTimeout(cleanup, 360);

    if (this._escapeHandler) {
      document.removeEventListener('keydown', this._escapeHandler);
      this._escapeHandler = null;
    }

    // Clear state after slide-out transition, move overlay back
    setTimeout(() => {
      if (!this.isOpen) {
        this.input.value = '';
        this.searchTerm = '';
        this.clearResults();
        this.selectedIndex = -1;
        this.overlay.style.top = '';
        this.overlay.style.height = '';
        this.appendChild(this.overlay);
      }
    }, 250);
  }

  positionPanel() {
    // Overlay starts at top: 0 on all viewports, no positioning needed
  }

  onChange() {
    const newTerm = this.input.value.trim();
    this.searchTerm = newTerm;

    if (newTerm.length < 2) {
      this.clearResults();
      return;
    }

    this.getSearchResults(newTerm);
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.toLowerCase().replace(/\s+/g, '-');

    // Abort previous request
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    // Return cached results
    if (this.cachedResults[queryKey]) {
      this.renderResults(this.cachedResults[queryKey]);
      return;
    }

    // Hide defaults + show results container before skeleton
    this.hideDefaultSuggestions();
    this.showSkeleton();

    const url = `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=revolor-predictive-search&resources[type]=product&resources[limit]=6`;

    fetch(url, { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((text) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        const section = doc.querySelector('#shopify-section-revolor-predictive-search');
        if (!section) {
          this.hideSkeleton();
          return;
        }
        const html = section.innerHTML;
        this.cachedResults[queryKey] = html;
        this.renderResults(html);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        this.hideSkeleton();
      });
  }

  renderResults(html) {
    this.hideSkeleton();
    this.hideDefaultSuggestions();
    if (!this.resultsContainer) return;
    this.resultsContainer.innerHTML = html;
    this.selectedIndex = -1;

    // Move view-all button to fixed slot at panel bottom
    const viewAll = this.resultsContainer.querySelector('.rps__view-all');
    if (viewAll && this.viewAllSlot) {
      this.viewAllSlot.innerHTML = '';
      this.viewAllSlot.appendChild(viewAll);
    }

    // Update live region for screen readers
    const countEl = this.resultsContainer.querySelector('[data-rps-live-region-count]');
    if (countEl && this.statusElement) {
      this.statusElement.textContent = countEl.textContent;
      this.statusElement.setAttribute('aria-hidden', 'false');
    }
  }

  showSkeleton() {
    if (this.skeleton) this.skeleton.removeAttribute('hidden');
    // Clear previous results while loading
    if (!this.resultsContainer) return;
    const existingResults = this.resultsContainer.querySelector('#revolor-search-results');
    if (existingResults) existingResults.remove();
  }

  hideSkeleton() {
    if (this.skeleton) this.skeleton.setAttribute('hidden', '');
  }

  clearResults() {
    if (!this.resultsContainer) return;
    // Keep skeleton markup, remove fetched results
    const existingResults = this.resultsContainer.querySelector('#revolor-search-results');
    if (existingResults) existingResults.remove();
    const liveRegion = this.resultsContainer.querySelector('[data-rps-live-region-count]');
    if (liveRegion) liveRegion.remove();
    if (this.viewAllSlot) this.viewAllSlot.innerHTML = '';
    this.hideSkeleton();
    this.showDefaultSuggestions();
    this.selectedIndex = -1;
  }

  showDefaultSuggestions() {
    if (this.defaultContent) {
      this.loadDefaultProducts();
      this.defaultContent.removeAttribute('hidden');
    }
    if (this.resultsContainer) this.resultsContainer.setAttribute('hidden', '');
  }

  hideDefaultSuggestions() {
    if (this.defaultContent) this.defaultContent.setAttribute('hidden', '');
    if (this.resultsContainer) this.resultsContainer.removeAttribute('hidden');
  }

  loadDefaultProducts() {
    if (this.defaultProductsLoaded || !this.defaultProductsContainer || !this.defaultProductsTemplate) return;
    this.defaultProductsContainer.innerHTML = this.defaultProductsTemplate.innerHTML;
    this.defaultProductsLoaded = true;
  }

  onKeydown(event) {
    const { key } = event;

    if (key === 'Escape') {
      this.close();
      return;
    }

    if (!this.resultsContainer) return;
    const allOptions = [...this.resultsContainer.querySelectorAll('[role="option"]')];
    if (!allOptions.length) return;

    if (key === 'ArrowDown') {
      event.preventDefault();
      this.selectedIndex = (this.selectedIndex + 1) % allOptions.length;
      this.updateSelection(allOptions);
    } else if (key === 'ArrowUp') {
      event.preventDefault();
      this.selectedIndex = this.selectedIndex <= 0 ? allOptions.length - 1 : this.selectedIndex - 1;
      this.updateSelection(allOptions);
    } else if (key === 'Enter' && this.selectedIndex >= 0) {
      event.preventDefault();
      const selected = allOptions[this.selectedIndex];
      const link = selected.tagName === 'A' ? selected : selected.querySelector('a');
      if (link) link.click();
    }
  }

  updateSelection(options) {
    options.forEach((opt, i) => {
      opt.setAttribute('aria-selected', i === this.selectedIndex ? 'true' : 'false');
    });

    if (this.selectedIndex >= 0) {
      const active = options[this.selectedIndex];
      this.input.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    } else {
      this.input.removeAttribute('aria-activedescendant');
    }
  }

  onFormSubmit(event) {
    if (!this.input.value.trim().length) {
      event.preventDefault();
      return;
    }

    // If keyboard-selected option exists, navigate there instead of default search
    if (!this.resultsContainer) return;
    const selected = this.resultsContainer.querySelector('[aria-selected="true"]');
    if (selected) {
      event.preventDefault();
      const link = selected.tagName === 'A' ? selected : selected.querySelector('a');
      if (link) link.click();
    }
  }
}

customElements.define('revolor-search', RevolorSearch);
